import React, { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import { StyledFormElementWrapper } from 'theme/styles';
import { StyledAutocomplete, StyledInnerWrapper } from './styles';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { useTranslations } from 'hooks/useTranslations';
import { IFormElementOptionProp, IFormElementProps } from 'models/form';
import { getNestedErrorMessage } from './helpers';
import { colors } from 'theme/colors';

const useDebounce = <T,>(value: T, delay = 500): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
};

interface IFormSearchSelect extends IFormElementProps {
  options: IFormElementOptionProp[];
  disabled?: boolean;
  required?: boolean;
  withValidation?: boolean;
  withLabel?: boolean;
  setValueAdditionalBehavior?: (value: any) => void;
  className?: string;
  arrayName?: string;
  arrayIndex?: number;
  fieldName?: string;
  onSelect?: (arg: any) => void;
  translate?: boolean;
  canBeEmpty?: boolean;
  showArrow?: boolean;
  onSearchChange?: (value: string) => void;
  searchDebounce?: number;
}

export const FormSearchSelect = ({
  name,
  label,
  options,
  control,
  errors,
  disabled,
  required,
  className,
  setValueAdditionalBehavior,
  arrayName,
  arrayIndex,
  fieldName,
  withValidation = true,
  withLabel = true,
  onSelect,
  translate = false,
  canBeEmpty = true,
  showArrow = false,
  onSearchChange,
  searchDebounce = 500,
}: IFormSearchSelect) => {
  const { t } = useTranslations();
  const [error, setError] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<IFormElementOptionProp | null>(null);
  const debouncedSearch = useDebounce(searchValue, searchDebounce);
  const isInitialSearch = useRef(true);

  useEffect(() => {
    if (isInitialSearch.current) {
      isInitialSearch.current = false;
      return;
    }
    onSearchChange?.(debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    const fieldValue = control._getWatch()[name];
    const selectedOption = options.find((o) => o.value === fieldValue);
    if (selectedOption) {
      setInputValue(translate ? t(selectedOption.label) : selectedOption.label);
    } else {
      setInputValue('');
    }
  }, [control, name, options, translate, t]);

  useEffect(() => {
    let isError = false;
    if (name.includes('.') && !name.includes('[') && Object.keys(errors).length) {
      const keys = name.split('.');
      const errorMessage = getNestedErrorMessage(errors, keys);
      if (errorMessage) {
        isError = true;
        setError(errorMessage);
      }
    } else {
      const errorMessage =
        arrayName && arrayIndex !== undefined && fieldName
          ? errors?.[arrayName]?.[arrayIndex]?.[fieldName]?.message
          : errors[name]?.message;
      if (errorMessage) {
        isError = true;
        setError(errorMessage);
      }
    }
    if (!isError) setError('');
  }, [errors, name, arrayName, arrayIndex, fieldName]);

  return (
    <StyledFormElementWrapper className={className}>
      {withLabel && (
        <FormLabel name={name} label={label} disabled={disabled} required={required} />
      )}

      <StyledInnerWrapper>
        <Controller
          name={name}
          control={control}
          defaultValue=""
          render={({ field }) => {
            const currentOption =
              options.find((o) => o.value === field.value) ||
              (selectedOption?.value === field.value ? selectedOption : null);
            const availableOptions =
              currentOption && !options.some((o) => o.value === currentOption.value)
                ? [currentOption, ...options]
                : options;
            return (
              <StyledAutocomplete
                id={name}
                disabled={disabled}
                popupIcon={showArrow ? undefined : null}
                options={availableOptions}
                value={currentOption}
                getOptionLabel={(option: any) => (translate ? t(option.label) : option.label)}
                isOptionEqualToValue={(option: any, value: any) =>
                  option.value === value.value
                }
                disableClearable={!canBeEmpty}
                filterOptions={onSearchChange ? (option: any) => option : undefined}
                onInputChange={(_, value, reason) => {
                  if (!onSearchChange) return;
                  if (reason === 'input') {
                    setSearchValue(value);
                  } else if (reason === 'clear') {
                    setSearchValue('');
                  }
                }}
                onChange={(_, newValue: any) => {
                  if (!newValue) {
                    setSelectedOption(null);
                    field.onChange('');
                    setValueAdditionalBehavior?.('');
                    onSelect?.('');
                  } else {
                    setSelectedOption(newValue);
                    field.onChange(newValue.value);
                    setValueAdditionalBehavior?.(newValue.value);
                    onSelect?.(newValue.value);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: colors.gray900,
                      },
                      '& .MuiInputBase-input.Mui-disabled': {
                        WebkitTextFillColor: colors.gray400,
                      },
                    }}
                    placeholder={canBeEmpty ? t('formSelect.all.placeholder') : ''}
                    error={!!error}
                  />
                )}
                renderOption={(props, option: any) => (
                  <li {...props} value={option.value} data-value={option.value}>
                    {translate ? t(option.label) : option.label}
                  </li>
                )}
              />
            );
          }}
        />
      </StyledInnerWrapper>

      {withValidation && <FormErrorLabel label={error} />}
    </StyledFormElementWrapper>
  );
};
